import React, { useState, useEffect } from 'react';
import { formatTime12h } from '../utils';

export default function AdminDashboard({ students, pools, batches, enrollments, setActiveTab, setModalConfig }) {
  // View states: 'renewals', 'expired', 'batches', 'students', 'activeSessions'
  const [activeView, setActiveView] = useState('activeSessions'); 
  const [selectedPoolFilter, setSelectedPoolFilter] = useState('all');

  // --- LIVE API STATE ---
  const [liveStudents, setLiveStudents] = useState([]);
  const [isFetchingLive, setIsFetchingLive] = useState(false);
  
  // State to force UI updates every minute so the "duration logged in" ticks up
  const [currentTime, setCurrentTime] = useState(Date.now());

  // --- GLOBAL FILTERING LOGIC ---
  const displayedBatches = selectedPoolFilter === 'all' 
    ? batches 
    : batches.filter(b => b.poolId === selectedPoolFilter);
  
  const displayedBatchIds = displayedBatches.map(b => b.id || b._id);
  const displayedEnrollments = enrollments.filter(e => displayedBatchIds.includes(e.batchId));

  const renewalsDue = displayedEnrollments.filter(e => Number(e.balanceHours) <= 8 && Number(e.balanceHours) > 0);
  const expiredPlans = displayedEnrollments.filter(e => Number(e.balanceHours) <= 0);
  const activeBatches = displayedBatches.filter(b => b.isActive !== false && b.active !== false);
  
  const activeStudentIds = [...new Set(displayedEnrollments.map(e => e.studentId))];
  const activePoolStudents = students.filter(s => activeStudentIds.includes(s.id || s._id));

  // --- FETCH LIVE STUDENTS FROM API ---
  useEffect(() => {
    const fetchLiveStudents = async () => {
      setIsFetchingLive(true);
      try {
        const response = await fetch(`https://tsaapis.operate.live/api/pools/current-users/${selectedPoolFilter}`, {
            credentials: 'include'
          });
        const result = await response.json();
        
        if (result.success) {
          setLiveStudents(result.data);
        } else {
          setLiveStudents([]);
        }
      } catch (error) {
        console.error("Failed to fetch live students:", error);
        setLiveStudents([]);
      } finally {
        setIsFetchingLive(false);
      }
    };

    fetchLiveStudents();

    const interval = setInterval(fetchLiveStudents, 60000);
    return () => clearInterval(interval);
  }, [selectedPoolFilter]);

  // --- DURATION TIMER ---
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);

  const getDuration = (loginTime) => {
    const diffMs = currentTime - new Date(loginTime).getTime();
    const diffMins = Math.max(0, Math.floor(diffMs / 60000));
    if (diffMins < 60) return `${diffMins} min`;
    const hrs = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hrs}h ${mins}m`;
  };

  // --- CAPACITY & UTILISATION LOGIC ---
  const totalCapacity = selectedPoolFilter === 'all' 
    ? pools.reduce((sum, p) => sum + (Number(p.Capacity || p.capacity) || 50), 0)
    : Number(pools.find(p => (p._id || p.id) === selectedPoolFilter)?.Capacity || pools.find(p => (p._id || p.id) === selectedPoolFilter)?.capacity) || 50;

  const currentUtilisation = activeView === 'activeSessions' ? liveStudents.length : activePoolStudents.length;
  const utilisationPercent = totalCapacity > 0 ? Math.min(100, Math.round((currentUtilisation / totalCapacity) * 100)) : 0;

  const getProgressColor = (percent) => {
    if (percent >= 90) return '#ef4444'; // Red (Nearing capacity)
    if (percent >= 75) return '#f59e0b'; // Amber/Orange
    return '#10b981'; // Green
  };

  const renderTableContent = () => {
    switch(activeView) {
      case 'batches':
        return (
          <>
            {/* UPDATED HEADER: Added Utilisation column */}
            <thead><tr><th>Batch Title</th><th>Type</th><th>Timings</th><th>Utilisation</th><th>Fees</th></tr></thead>
            <tbody>
              {activeBatches.map(b => {
                const bId = b._id || b.id;
                const bCapacity = Number(b.Capacity || b.capacity) || 20; // Defaulting to 20
                
                // Calculate Utilisation for this specific batch
                const batchUtilisation = enrollments.filter(e => e.batchId === bId).length;
                const batchUtilisationPercent = bCapacity > 0 ? Math.min(100, Math.round((batchUtilisation / bCapacity) * 100)) : 0;

                return (
                  <tr key={bId}>
                    <td><strong>{b.Title || b.title}</strong></td>
                    <td><span className="badge">{b.Type || b.type}</span></td>
                    <td>{formatTime12h(b.StartTime || b.startTime)} - {formatTime12h(b.EndTime || b.endTime)}</td>
                    
                    {/* NEW: Utilisation Progress Bar Cell */}
                    <td style={{ minWidth: '130px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-gray)', marginBottom: '5px', fontWeight: 'bold' }}>
                        <span>{batchUtilisation} Enrolled</span>
                        <span>{bCapacity} Max</span>
                      </div>
                      <div style={{ width: '100%', height: '6px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{
                          height: '100%',
                          width: `${batchUtilisationPercent}%`,
                          backgroundColor: getProgressColor(batchUtilisationPercent),
                          transition: 'width 0.5s ease-out, background-color 0.5s ease'
                        }}></div>
                      </div>
                    </td>

                    <td style={{color:'var(--accent-gold)'}}>₹{b.Fees || b.fees}</td>
                  </tr>
                );
              })}
            </tbody>
          </>
        );
      case 'expired':
        return (
          <>
            <thead><tr><th>Student</th><th>Batch</th><th>Expired On</th><th>Contact</th><th>Action</th></tr></thead>
            <tbody>
              {expiredPlans.length === 0 ? <tr><td colSpan="5" style={{textAlign:'center'}}>No expired plans.</td></tr> : 
                expiredPlans.map(enroll => {
                  const student = students.find(s => (s.id || s._id) === enroll.studentId);
                  const batch = batches.find(b => (b.id || b._id) === enroll.batchId);
                  if(!student || !batch) return null;
                  return (
                    <tr key={enroll.id || enroll._id}>
                      <td><strong>{student.name}</strong><br/><small>{student.regId}</small></td>
                      <td>{batch.Title || batch.title}</td>
                      <td style={{color: '#64748b'}}>{enroll.endDay || enroll.ExpiryDate}</td>
                    <td>{student.mobile}<br /><small style={{ color: '#64748b' }}>{student.email}</small></td>
                      <td><button className="btn-small btn-action" onClick={() => alert(`Contacting ${student.name} for win-back...`)}>Follow Up</button></td>
                    </tr>
                  )
                })
              }
            </tbody>
          </>
        );
      case 'renewals':
      default:
        return (
          <>
            <thead><tr><th>Student</th><th>Enrolled Batch</th><th>Status</th><th>Contact</th><th>Action</th></tr></thead>
            <tbody>
              {renewalsDue.length === 0 ? <tr><td colSpan="5" style={{textAlign:'center'}}>No upcoming renewals.</td></tr> : 
                renewalsDue.map(enroll => {
                  const student = students.find(s => (s.id || s._id) === enroll.studentId);
                  const batch = batches.find(b => (b.id || b._id) === enroll.batchId);
                  if(!student || !batch) return null;
                  return (
                    <tr key={enroll.id || enroll._id}>
                      <td><strong>{student.name}</strong><br/><small>{student.regId}</small></td>
                      <td><span className="badge" style={{marginBottom:'5px', display:'inline-block'}}>{batch.Type || batch.type}</span><br/>{batch.Title || batch.title}</td>
                      <td><strong style={{color: '#ef4444', fontSize: '1.1rem'}}>{enroll.balanceHours} Hrs Left</strong><br/><small>Expires: {enroll.endDay || enroll.ExpiryDate}</small></td>
                    <td>{student.mobile}<br /><small style={{ color: '#64748b' }}>{student.email}</small></td>
                      <td>
                        <button className="btn-small" style={{ backgroundColor: '#25D366', color: 'white', border: 'none' }} onClick={() => alert(`Sending WhatsApp reminder to ${student.name}...`)}>
                          <i className="fab fa-whatsapp"></i> Remind
                        </button>
                      </td>
                    </tr>
                  )
                })
              }
            </tbody>
          </>
        );
    }
  };

  const getTableTitle = () => {
    switch(activeView) {
      case 'activeSessions': return "Currently In Pool";
      case 'batches': return "Active Batches";
      case 'expired': return "Expired Enrollments";
      case 'renewals': return "Priority Renewals";
      case 'students': return "Active Members";
      default: return "";
    }
  };

  return (
    <div className="programs-container">
      
      {/* RESPONSIVE STYLES */}
      <style>{`
        /* --- MOBILE FIRST DEFAULT --- */
        .dashboard-main-layout {
          display: flex;
          flex-direction: column;
          gap: 15px;
          width: 100%;
          height: 100%;
          padding: 10px;
        }
        
        .desktop-only { display: none; }
        
        .mobile-only {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .sidebar-container {
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          padding: 10px 0px 0px 10px;
        }

        .metrics-list-responsive {
          display: flex;
          flex-direction: row;
          overflow-x: auto;
          gap: 10px;
          padding-bottom: 5px;
          scrollbar-width: none; /* Firefox */
        }
        .metrics-list-responsive::-webkit-scrollbar { display: none; }

        .content-container {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-height: 0; /* Crucial for internal scrolling */
        }

        /* --- LAPTOP / DESKTOP OVERRIDE --- */
        @media (min-width: 768px) {
          .dashboard-main-layout {
            flex-direction: row;
            gap: 20px;
            height: calc(100vh - 40px); 
            max-width: 1600px; 
            margin: 20px auto;
            padding: 0;
          }
          
          .mobile-only { display: none; }
          .desktop-only { display: block; }
          
          .sidebar-container {
            flex: 0 0 250px !important; 
            width: 250px !important;
            max-width: 250px !important;
            padding: 15px; 
          }
          
          .metrics-list-responsive {
            flex-direction: column;
            overflow-x: hidden;
            overflow-y: auto;
            gap: 5px;
          }
        }
      `}</style>

      {/* MOBILE ONLY TOP HEADER */}
      <div className="mobile-only">
        <h2 style={{ margin: 0, fontSize: '1.2rem' }}>Business Overview</h2>
        <select 
          value={selectedPoolFilter}
          onChange={(e) => setSelectedPoolFilter(e.target.value)}
          style={{ backgroundColor: 'rgba(0,0,0,0.3)', color: 'var(--accent-gold)', border: '1px solid rgba(255,255,255,0.2)', padding: '6px 12px', borderRadius: '5px', fontSize: '0.9rem', outline: 'none' }}
        >
          <option value="all">All Pools</option>
          {pools.map(p => <option key={p._id || p.id} value={p._id || p.id}>{p.Name || p.name}</option>)}
        </select>
      </div>

      {/* LEFT SIDEBAR (Metrics & Laptop Dropdown) */}
      <div className="admin-card sidebar-container">
        
        {/* DESKTOP ONLY HEADER & DROPDOWN */}
        <div className="desktop-only">
          <h2>
             <select 
              value={selectedPoolFilter}
              onChange={(e) => setSelectedPoolFilter(e.target.value)}
              style={{ width: '100%', backgroundColor: 'transparent', color: 'var(--accent-gold)', border: 'none', padding: '5px 0', fontSize: '0.95rem', outline: 'none', cursor: 'pointer', textOverflow: 'ellipsis' }}
            >
              <option value="all">All Pools</option>
              {pools.map(p => <option key={p._id || p.id} value={p._id || p.id}>{p.Name || p.name}</option>)}
            </select>
          </h2>
        </div>

        {/* METRICS LIST (Horizontal on Mob, Vertical on Laptop) */}
        <div className="pools-list-container metrics-list-responsive">
          
          <div
            className={`pool-list-item ${activeView === 'activeSessions' ? 'active' : ''}`}
            onClick={() => setActiveView('activeSessions')}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px' }}
          >
            <div style={{ paddingRight: '5px' }}>
              <strong style={{ display: 'block', fontSize: '1rem', color: activeView === 'activeSessions' ? 'var(--accent-gold)' : 'white' }}>
                <i className="fas fa-swimmer" style={{ color: '#3b82f6', width: '22px' }}></i> Live
              </strong>
              <small style={{ color: 'var(--text-gray)', display: 'block', marginTop: '2px', fontSize: '0.8rem' }}>In Pool</small>
            </div>
            <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#3b82f6' }}>
              {isFetchingLive ? '...' : liveStudents.length}
            </span>
          </div>

          <div
            className={`pool-list-item ${activeView === 'students' ? 'active' : ''}`}
            onClick={() => setActiveView('students')}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px' }}
          >
            <div style={{ paddingRight: '5px' }}>
              <strong style={{ display: 'block', fontSize: '1rem', color: activeView === 'students' ? 'var(--accent-gold)' : 'white' }}>
                <i className="fas fa-users" style={{ color: '#f59e0b', width: '22px' }}></i> Directory
              </strong>
              <small style={{ color: 'var(--text-gray)', display: 'block', marginTop: '2px', fontSize: '0.8rem' }}>Total Enrolled</small>
            </div>
            <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#f59e0b' }}>{activePoolStudents.length}</span>
          </div>

          <div
            className={`pool-list-item ${activeView === 'renewals' ? 'active' : ''}`}
            onClick={() => setActiveView('renewals')}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px' }}
          >
            <div style={{ paddingRight: '5px' }}>
              <strong style={{ display: 'block', fontSize: '1rem', color: activeView === 'renewals' ? 'var(--accent-gold)' : 'white' }}>
                <i className="fas fa-exclamation-circle" style={{ color: '#ef4444', width: '22px' }}></i> Action
              </strong>
              <small style={{ color: 'var(--text-gray)', display: 'block', marginTop: '2px', fontSize: '0.8rem' }}>Renewals Due</small>
            </div>
            <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#ef4444' }}>{renewalsDue.length}</span>
          </div>

          <div
            className={`pool-list-item ${activeView === 'expired' ? 'active' : ''}`}
            onClick={() => setActiveView('expired')}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px' }}
          >
            <div style={{ paddingRight: '5px' }}>
              <strong style={{ display: 'block', fontSize: '1rem', color: activeView === 'expired' ? 'var(--accent-gold)' : 'white' }}>
                <i className="fas fa-history" style={{ color: '#64748b', width: '22px' }}></i> Win-back
              </strong>
              <small style={{ color: 'var(--text-gray)', display: 'block', marginTop: '2px', fontSize: '0.8rem' }}>Expired Plans</small>
            </div>
            <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#64748b' }}>{expiredPlans.length}</span>
          </div>

          <div
            className={`pool-list-item ${activeView === 'batches' ? 'active' : ''}`}
            onClick={() => setActiveView('batches')}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px' }}
          >
            <div style={{ paddingRight: '5px' }}>
              <strong style={{ display: 'block', fontSize: '1rem', color: activeView === 'batches' ? 'var(--accent-gold)' : 'white' }}>
                <i className="fas fa-layer-group" style={{ color: '#10b981', width: '22px' }}></i> Batches
              </strong>
              <small style={{ color: 'var(--text-gray)', display: 'block', marginTop: '2px', fontSize: '0.8rem' }}>Schedule</small>
            </div>
            <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#10b981' }}>{activeBatches.length}</span>
          </div>

        </div>
      </div>

      {/* RIGHT CONTENT AREA */}
      <div className="admin-card content-container">
        
        {/* Dynamic Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '10px' }}>
          <h2 style={{ margin: 0, fontSize: '1.3rem', border: 'none', padding: 0, display: 'flex', alignItems: 'center', color: activeView === 'renewals' ? '#ef4444' : activeView === 'activeSessions' ? '#3b82f6' : 'white' }}>
            {activeView === 'activeSessions' && <i className="fas fa-circle" style={{marginRight: '10px', fontSize: '0.7rem', color: '#3b82f6', animation: 'blink 2s infinite'}}></i>}
            {getTableTitle()}
          </h2>

          {/* PROGRESS BAR FOR CAPACITY (Visible only in Active Sessions and Active Members) */}
          {(activeView === 'activeSessions' || activeView === 'students') && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: '1 1 200px', maxWidth: '350px', justifyContent: 'flex-end' }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-gray)', whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                <span style={{ color: 'white' }}>{currentUtilisation}</span> / {totalCapacity} ({utilisationPercent}%)
              </div>
              <div style={{ flex: 1, maxWidth: '150px', height: '8px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${utilisationPercent}%`,
                  backgroundColor: getProgressColor(utilisationPercent),
                  transition: 'width 0.5s ease-out, background-color 0.5s ease'
                }}></div>
              </div>
            </div>
          )}
        </div>
        
        {/* Scrollable View Port */}
        <div style={{ flex: 1, overflowY: 'auto', paddingRight: '5px' }}>
          {activeView === 'activeSessions' ? (
            <>
              { isFetchingLive && liveStudents.length === 0 ? (
                <p style={{ color: '#3b82f6', textAlign: 'center', marginTop: '2rem' }}>Scanning pool...</p>
              ) : liveStudents.length === 0 ? (
                <p style={{ color: 'var(--text-gray)', textAlign: 'center', marginTop: '2rem' }}>The pool is currently empty.</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '15px', marginTop:"1rem" }}>
                  {liveStudents.map(session => {
                    const avatarUrl = session.picUrl || `https://ui-avatars.com/api/?name=${session.name.split(' ')[0]}+${session.name.split(' ')[1]||''}&background=3b82f6&color=fff&size=300`;
                    return (
                      <div 
                        key={session.attendanceId} 
                        onClick={() => setModalConfig({ isOpen: true, type: 'student-info', studentId: session._id })}
                        style={{ 
                          backgroundImage: `linear-gradient(to top, rgba(15, 23, 42, 1) 0%, rgba(15, 23, 42, 0.4) 50%, rgba(15, 23, 42, 0.7) 100%), url(${avatarUrl})`,
                          backgroundSize: 'cover', backgroundPosition: 'center', padding: '15px', borderRadius: '12px', 
                          border: '1px solid rgba(59, 130, 246, 0.6)', 
                          textAlign: 'center', position: 'relative', transition: 'transform 0.2s, box-shadow 0.2s', cursor: 'pointer',
                          display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', minHeight: '160px', 
                          boxShadow: '0 0 15px rgba(59, 130, 246, 0.2)'
                        }} 
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 10px 20px rgba(59, 130, 246, 0.4)'; }} 
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 0 15px rgba(59, 130, 246, 0.2)'; }}
                      >
                        <div style={{ position: 'absolute', top: '10px', left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', backgroundColor: 'rgba(59, 130, 246, 0.9)', backdropFilter: 'blur(4px)', padding: '4px 10px', borderRadius: '20px', border: '1px solid rgba(255, 255, 255, 0.3)', maxWidth: '90%', width: 'max-content' }}>
                          <span style={{ fontSize: '0.6rem', color: '#fff', fontWeight: 'bold', textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {session.currentBatch || 'Open Swim'}
                          </span>
                        </div>
                        <div style={{ position: 'absolute', top: '40px', left: '50%', transform: 'translateX(-50%)', backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', padding: '3px 8px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <i className="fas fa-clock" style={{ fontSize: '0.6rem', color: '#3b82f6' }}></i>
                            <span style={{ fontSize: '0.7rem', color: '#fff', fontWeight: 'bold', letterSpacing: '0.5px' }}>{getDuration(session.loginTime)}</span>
                        </div>
                        <div style={{ position: 'relative', zIndex: 2 }}>
                          <h4 style={{ margin: '0 0 2px 0', color: 'white', fontSize: '1rem', textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>{session.name}</h4>
                          <p style={{ margin: 0, color: '#3b82f6', fontSize: '0.8rem', textShadow: '0 1px 2px rgba(0,0,0,0.8)', fontWeight: 'bold' }}>
                            <i className="fas fa-phone-alt" style={{ fontSize: '0.7rem', marginRight: '4px' }}></i> {session.mobile}
                          </p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </>
          ) : activeView === 'students' ? (
            <>
              {activePoolStudents.length === 0 ? (
                <p style={{ color: 'var(--text-gray)', textAlign: 'center', marginTop: '2rem' }}>No students found for this selection.</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '15px' , marginTop:"1rem" }}>
                  {activePoolStudents.map(s => {
                    const studentEnrollment = enrollments.find(e => e.studentId === s.id && displayedBatchIds.includes(e.batchId));
                    const activeBatch = batches.find(b => (b.id || b._id) === studentEnrollment?.batchId);
                    const batchTitle = activeBatch ? (activeBatch.Title || activeBatch.title) : 'Active';
                    const avatarUrl = s.profilePic || `https://ui-avatars.com/api/?name=${s.name.replace(' ', '+')}&background=fbbf24&color=0f172a&size=300`;

                    return (
                      <div 
                        key={s.id || s._id} 
                        onClick={() => setModalConfig({ isOpen: true, type: 'student-info', studentId: s.id || s._id })}
                        style={{ 
                          backgroundImage: `linear-gradient(to top, rgba(15, 23, 42, 1) 0%, rgba(15, 23, 42, 0.4) 50%, rgba(15, 23, 42, 0.7) 100%), url(${avatarUrl})`,
                          backgroundSize: 'cover', backgroundPosition: 'center', padding: '15px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', textAlign: 'center', position: 'relative', transition: 'transform 0.2s, box-shadow 0.2s', cursor: 'pointer', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', minHeight: '160px', boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
                        }} 
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 10px 15px rgba(0,0,0,0.5)'; }} 
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.3)'; }}
                      >
                        <div style={{ position: 'absolute', top: '10px', left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', backgroundColor: 'rgba(16, 185, 129, 0.9)', backdropFilter: 'blur(4px)', padding: '4px 10px', borderRadius: '20px', border: '1px solid rgba(255, 255, 255, 0.3)', maxWidth: '90%', width: 'max-content' }}>
                          <div style={{ width: '6px', height: '6px', backgroundColor: '#fff', borderRadius: '50%', boxShadow: '0 0 8px #fff', animation: 'blink 2s infinite', flexShrink: 0 }}></div>
                          <span style={{ fontSize: '0.6rem', color: '#fff', fontWeight: 'bold', textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={batchTitle}>
                            {batchTitle}
                          </span>
                        </div>
                        <div style={{ position: 'relative', zIndex: 2 }}>
                          <h4 style={{ margin: '0 0 2px 0', color: 'white', fontSize: '1rem', textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>{s.name}</h4>
                          <p style={{ margin: 0, color: '#cbd5e1', fontSize: '0.8rem', textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>
                            <i className="fas fa-phone-alt" style={{ fontSize: '0.7rem', marginRight: '4px' }}></i> {s.mobile}
                          </p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </>
          ) : (
            <div className="table-responsive" style={{ minHeight: '0' }}>
              <table style={{ background: 'transparent', width: '100%', borderCollapse: 'collapse' }}>
                {renderTableContent()}
              </table>
            </div>
          )}
        </div>
      </div>
      
    </div>
  );
}